import React from 'react';
import { useFormik } from 'formik';
import { Button, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import SelectField from 'components/SelectField';
import ConfirmDialog from 'components/ConfirmDialog';
import UserRoleType from 'types/enums/UserRoleType';
import UserFormProps from 'types/forms/UserFormProps';
import User from 'types/models/User';
import { getRefundAuthorizationCode, storeUser, updateUser } from 'services/userServices';
import { CreateForm, EditForm } from './schemas/UserForm';

export default ({ user, onSuccess }: UserFormProps): JSX.Element => {
  const { authData } = React.useContext(AppContext);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [error, setError] = React.useState('');
  const { t } = useTranslation();
  const editorRoles = [UserRoleType.CommerceEditor, UserRoleType.VenueEditor];
  const roles = [
    { label: t('listUsers-page-commerce-user'), value: UserRoleType.CommerceEditor },
    { label: t('listUsers-page-venue-user'), value: UserRoleType.VenueEditor },
    { label: t('listUsers-page-franchise-user'), value: UserRoleType.FranchiseEditor },
  ];

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const onConfirm = async (): Promise<void> => {
    const userData = {
      email: formik.values.email,
      password: formik.values.password,
      name: formik.values.name,
      role: formik.values.role as UserRoleType,
      refundAuthorizationCode: formik.values.refundAuthorizationCode ?? null,
    };

    try {
      let result: User | null;

      if (user) {
        result = await updateUser(user.id, userData);
      } else {
        result = await storeUser(userData);
      }

      if (result) {
        onSuccess();
      }
    } catch (error: any) {
      console.log(error);
      setError(`${error}`);
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: user?.email ?? '',
      password: '',
      name: user?.name ?? '',
      role: authData?.user.role === UserRoleType.VenueEditor ? UserRoleType.CommerceEditor : user?.role ?? '',
      refundAuthorizationCode: user?.refundAuthorizationCode ?? '',
    },
    validationSchema: user ? EditForm(t) : CreateForm(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  const changeRefundAuthorizationCode = async () => {
    const newCode = await getRefundAuthorizationCode();
    formik.setFieldValue('refundAuthorizationCode', newCode);
  };

  return (
    <>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <TextField
          margin="dense"
          fullWidth
          id="email"
          name="email"
          label={t('user-form-email')}
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          disabled={formik.isSubmitting}
        />
        <TextField
          margin="dense"
          fullWidth
          id="password"
          name="password"
          type="password"
          label={t('user-form-password')}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          disabled={formik.isSubmitting}
        />
        <TextField
          margin="dense"
          fullWidth
          id="name"
          name="name"
          label={t('user-form-name')}
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          disabled={formik.isSubmitting}
        />
        {authData?.user.role !== UserRoleType.VenueEditor && (
          <SelectField
            name="role"
            label={t('user-form-role')}
            placeholder={t('user-form-select-role-placeholder')}
            value={formik.values.role}
            options={roles}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.role}
            error={formik.errors.role}
            isClearable={true}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable={true}
          />
        )}

        {editorRoles.includes(formik.values.role as UserRoleType) && (
          <>
            <TextField
              margin="dense"
              fullWidth
              id="refundAuthorizationCode"
              name="refundAuthorizationCode"
              label={t('user-form-refund-authorization-code')}
              value={formik.values.refundAuthorizationCode}
              onChange={formik.handleChange}
              error={formik.touched.refundAuthorizationCode && Boolean(formik.errors.refundAuthorizationCode)}
              helperText={formik.touched.refundAuthorizationCode && formik.errors.refundAuthorizationCode}
              disabled={true}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={formik.isSubmitting}
              onClick={() => changeRefundAuthorizationCode()}
            >
              {t('user-form-change-refund-authorization-code')}
            </Button>
          </>
        )}

        <div className="footer">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {`${user ? t('save-changes') : t('user-form-create')}`}
          </Button>
        </div>
        {error && (
          <div>
            <Typography color="error" variant="h6" component="h2">
              {error}
            </Typography>
          </div>
        )}
      </form>
    </>
  );
};
